import { useState } from 'react';
import { ChevronDown, ChevronUp, MessageCircle01, Paperclip } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import type { Container } from '@/types/container';
import { useI18n } from '@/hooks/use-i18n';
import { formatRelativeTime } from '@/utils/date-formatters';
import { ContainerTimeline } from './container-timeline';

interface ContainerListItemProps {
    container: Container;
    onSelect?: (containerId: string) => void;
    showComments?: boolean;
    showAttachments?: boolean;
}

export const ContainerListItem = ({
    container,
    onSelect,
    showComments = true,
    showAttachments = true
}: ContainerListItemProps) => {
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const [isExpanded, setIsExpanded] = useState(false);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'In Transit': return 'brand';
            case 'Delivered': return 'success';
            case 'Delayed': return 'error';
            case 'At Port': return 'warning';
            default: return 'gray';
        }
    };

    const getDocumentStatusColor = (status: string) => {
        switch (status) {
            case 'Completo': return 'success';
            case 'Pending': return 'warning';
            case 'Error': return 'error';
            default: return 'gray';
        }
    };

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    // Simula conteggio commenti e allegati (in futuro da dati reali)
    const commentsCount = 0; // TODO: da implementare
    const attachmentsCount = 0; // TODO: da implementare

    // Estrai waypoints per la timeline
    const waypoints = container.waypoints || [];


    return (
        <div className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary overflow-hidden">
            {/* Main Content - Background Bianco */}
            <div
                className="p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                onClick={() => onSelect?.(container.id)}
            >
                <div className="flex items-start justify-between gap-4">
                    {/* Left Section */}
                    <div className="flex-1 min-w-0">
                        {/* Header: Container Number + Status */}
                        <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-lg font-semibold text-primary">{container.containerNumber}</h4>
                            <Badge color={getStatusColor(container.status)} size="sm">
                                {translateContainerStatus(container.status)}
                            </Badge>
                        </div>
                    </div>

                    {/* Right Section: Comments, Attachments, Timestamp, Toggle */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                        {/* Comments & Attachments */}
                        <div className="flex items-center gap-3 text-sm text-tertiary">
                            {showComments && (
                                <div className="flex items-center gap-1">
                                    <MessageCircle01 className="size-4" />
                                    <span>{commentsCount}</span>
                                </div>
                            )}
                            {showAttachments && (
                                <div className="flex items-center gap-1">
                                    <Paperclip className="size-4" />
                                    <span>{attachmentsCount}</span>
                                </div>
                            )}
                        </div>

                        {/* Timestamp */}
                        <div className="text-xs text-tertiary">
                            {formatRelativeTime(container.updatedAt)}
                        </div>

                        {/* Toggle Button */}
                        {!isExpanded && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsExpanded(!isExpanded);
                                }}
                                className="p-1 hover:bg-secondary rounded transition-colors"
                                aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                            >
                                <ChevronDown className="size-5 text-tertiary" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex items-start justify-between gap-4">
                    {/* Left Section */}
                    <div className="flex-1 min-w-0">
                        {/* Shipping Company */}
                        {container.shippingCompany && (
                            <div className="text-sm text-primary font-medium mb-3">
                                {container.shippingCompany}
                            </div>
                        )}

                        {/* Timeline - Visualizzazione stato vessel */}
                        <ContainerTimeline
                            container={container}
                            waypoints={waypoints}
                        />

                        {/* Badges */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                {translateDocumentStatus(container.documentStatus)}
                            </Badge>
                            <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                {translateRiskLevel(container.riskLevel)}
                            </Badge>
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded Details - Background Grigio */}
            {isExpanded && (
                <div className="bg-secondary p-4 border-t border-secondary_alt">
                    {/* Header con trigger per chiudere - come nell'immagine */}
                    <div className="flex items-center justify-end mb-4">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(false);
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-tertiary hover:text-primary hover:bg-secondary_alt rounded transition-colors"
                            aria-label="Collapse details"
                        >
                            <ChevronUp className="size-4" />
                            <span>Hide details</span>
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        {/* Booking & Documents */}
                        <div className="space-y-2">
                            {container.bookingNumber && (
                                <div>
                                    <span className="text-tertiary">Booking Number:</span>
                                    <span className="text-primary ml-2">{container.bookingNumber}</span>
                                </div>
                            )}
                            <div>
                                <span className="text-tertiary">MBL:</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                            <div>
                                <span className="text-tertiary">HBL:</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                            <div>
                                <span className="text-tertiary">Invoice Number:</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                        </div>

                        {/* Receiver Info */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">Receiver's Name:</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                            <div>
                                <span className="text-tertiary">Consignee:</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                            <div>
                                <span className="text-tertiary">Contact Phone:</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                        </div>

                        {/* Shipping Details */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">Hazardous Material:</span>
                                <span className="text-primary ml-2">No</span>
                            </div>
                            <div>
                                <span className="text-tertiary">Package Weight (kg):</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                            <div>
                                <span className="text-tertiary">Freight Cost ($USD):</span>
                                <span className="text-primary ml-2">
                                    {container.value.currency === 'USD'
                                        ? container.value.amount.toLocaleString()
                                        : '-'}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">Customs Inspection Date:</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">Seal Number:</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                            <div>
                                <span className="text-tertiary">Pallet Count:</span>
                                <span className="text-primary ml-2">-</span>
                            </div>
                            <div>
                                <span className="text-tertiary">Commodity:</span>
                                <span className="text-primary ml-2 capitalize">{container.commodity}</span>
                            </div>
                            <div>
                                <span className="text-tertiary">Value:</span>
                                <span className="text-primary ml-2">
                                    {container.value.currency} {container.value.amount.toLocaleString()}
                                </span>
                            </div>
                        </div>

                        {/* Progress & Dates */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">Progress:</span>
                                <span className="text-primary ml-2">{container.progress}%</span>
                            </div>
                            <div>
                                <span className="text-tertiary">Created:</span>
                                <span className="text-primary ml-2">
                                    {container.createdAt.toLocaleDateString('it-IT')}
                                </span>
                            </div>
                            <div>
                                <span className="text-tertiary">Updated:</span>
                                <span className="text-primary ml-2">
                                    {container.updatedAt.toLocaleDateString('it-IT')}
                                </span>
                            </div>
                            {container.isConfirmed !== undefined && (
                                <div>
                                    <span className="text-tertiary">Confirmed:</span>
                                    <span className="text-primary ml-2">
                                        {container.isConfirmed ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Supplier/PO Info */}
                        <div className="space-y-2">
                            <div>
                                <span className="text-tertiary">Supplier:</span>
                                <span className="text-primary ml-2">2 POs</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

